import React, { useEffect, useRef, useState } from "react";
import { Eye, EyeOff, Info } from "lucide-react";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Button } from "../../../ui/button";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../../../ui/tooltip";
import { useSelector } from "react-redux";
import { errorMsgSelector } from "../../../../store/helpers/helperSelectors";
import { setErrorMsg } from "../../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../../store/hooks";
import { store } from "../../../../store";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

interface PasswordFieldProps {
    label: string;
    name: string;
    value: string;
    required?: boolean;
    disabled?: boolean;
    placeholder?: string;
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    patternMsg?: string;
    passwordPolicyTitle?: string;
    tooltipText?: React.ReactNode;
    autoFocus?: boolean;
    onChange: (name: string, value: string) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export const PasswordField: React.FC<PasswordFieldProps> = ({
    label,
    name,
    value,
    required = false,
    disabled = false,
    placeholder,
    minLength,
    maxLength,
    pattern,
    patternMsg,
    passwordPolicyTitle,
    tooltipText,
    autoFocus,
    onChange,
    onBlur,
}) => {
    const [showPassword, setShowPassword] = useState(false);
    const dispatch = useStoreDispatch();
    const errorMsg = useSelector(errorMsgSelector);
    const error = errorMsg?.[name] || "";
    const inputRef = useRef<HTMLInputElement>(null);

    /* Focus on error */
    useEffect(() => {
        if (error && inputRef.current) {
            inputRef.current.focus();
        }
    }, [error]);

    /* ---- Pattern validation ---- */
    const checkPattern = (val: string): string => {
        if (pattern && val) {
            const regex = new RegExp(pattern);
            if (!regex.test(val)) {
                return patternMsg || `Invalid input. Please enter a valid ${name}`;
            }
        }
        return "";
    };

    /* ---- MinLength validation ---- */
    const checkMinLength = (val: string): string => {
        if (minLength !== undefined && val.length > 0 && val.length < minLength) {
            return `Minimum ${minLength} characters`;
        }
        return "";
    };

    /* ---- Inline validation on input ---- */
    const validate = (val: string) => {
        const existing = store.getState().helpers.errorMsg;
        // Clear previous error for this field
        const cleared: Record<string, string> = {};
        for (const [k, v] of Object.entries(existing)) {
            if (k !== name) cleared[k] = String(v ?? "");
        }
        dispatch(setErrorMsg(cleared));

        // Pattern check first
        const patternErr = checkPattern(val);
        if (patternErr) {
            dispatch(setErrorMsg({ ...cleared, [name]: patternErr }));
            return;
        }

        // MinLength check
        const minLenErr = checkMinLength(val);
        if (minLenErr) {
            dispatch(setErrorMsg({ ...cleared, [name]: minLenErr }));
        }
    };

    /* ---- onBlur: required + pattern + minLength ---- */
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const val = e.target.value;
        const existing = store.getState().helpers.errorMsg;

        // Pattern check
        const patternErr = checkPattern(val);
        if (patternErr) {
            dispatch(setErrorMsg({ ...existing, [name]: patternErr }));
            onBlur?.(e);
            return;
        }

        // MinLength check
        const minLenErr = checkMinLength(val);
        if (minLenErr) {
            dispatch(setErrorMsg({ ...existing, [name]: minLenErr }));
            onBlur?.(e);
            return;
        }

        // Required check
        if (required && !val) {
            dispatch(setErrorMsg({ ...existing, [name]: `${label} is required!.` }));
        }

        onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        validate(val);
        onChange(name, val);
    };

    const defaultPolicy = `The password must be at least ${minLength ?? 8} characters long and include at least one uppercase letter (A-Z), one digit (1-9), and one special non-alphabetic character (e.g.,?!%$#).`;

    const field = (
        <div className="flex flex-col gap-2.5 w-full">
            <Label htmlFor={name} className="text-sm font-medium mb-3">
                {label}
                {required && <span className="text-destructive m-0">*</span>}
            </Label>
            <div className="relative">
                <Input
                    ref={inputRef}
                    id={name}
                    name={name}
                    type={showPassword ? "text" : "password"}
                    value={value}
                    disabled={disabled}
                    required={required}
                    maxLength={maxLength}
                    placeholder={placeholder}
                    autoFocus={autoFocus}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={error ? "!border-destructive focus:!ring-destructive pr-10" : "pr-10"}
                />
                <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-transparent border-none cursor-pointer"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                    {showPassword ? (
                        <VisibilityOff sx={{ fontSize: 16 }} />
                    ) : (
                        <Visibility sx={{ fontSize: 16 }} />
                    )}
                </button>

                
            </div>
            {error && (
                <div className="flex items-center gap-1.5">
                    <p className="text-xs text-destructive m-0">{error}</p>
                    <TooltipProvider delayDuration={200}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Info className="h-3.5 w-3.5 text-destructive cursor-help shrink-0" />
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs">
                                {passwordPolicyTitle || defaultPolicy}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            )}
        </div>
    );

    if (!tooltipText) return field;

    return (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>{field}</TooltipTrigger>
                <TooltipContent side="top" align="end" sideOffset={-24} className="z-[1400]">
                    {tooltipText}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};
